import { CrmError } from "@sp-crm/core";
import { BASEMAP_MANIFEST_URL, BasemapManifest, parseBasemapManifest } from "./map-style";

export class BasemapManifestFetchError extends CrmError {
    readonly name = "BasemapManifestFetchError";
    readonly errorType = "BASEMAP_MANIFEST_FETCH";
    readonly skipReporting = true;

    constructor(public readonly originalError: Error) {
        super(`Basemap manifest fetch failed: ${originalError.message}`);
    }
}

export class BasemapManifestResponseError extends CrmError {
    readonly name = "BasemapManifestResponseError";
    readonly errorType = "BASEMAP_MANIFEST_RESPONSE";
    readonly skipReporting = false;

    constructor(public readonly status: number) {
        super(`Basemap manifest request failed: ${status}`);
    }
}

export class BasemapManifestContentError extends CrmError {
    readonly name = "BasemapManifestContentError";
    readonly errorType = "BASEMAP_MANIFEST_CONTENT";
    readonly skipReporting = false;

    constructor(reason: string) {
        super(`Basemap manifest unusable: ${reason}`);
    }
}

export const isAbortError = (e: unknown): boolean =>
    typeof e === "object" &&
    e !== null &&
    (e as { name?: unknown }).name === "AbortError";

const toError = (e: unknown): Error => (e instanceof Error ? e : new Error(String(e)));

const fetchManifestResponse = async (signal: AbortSignal): Promise<Response> => {
    try {
        return await fetch(BASEMAP_MANIFEST_URL, { signal });
    } catch (e: unknown) {
        if (isAbortError(e)) {
            throw e;
        }
        throw new BasemapManifestFetchError(toError(e));
    }
};

const readManifestText = async (response: Response): Promise<string> => {
    try {
        return await response.text();
    } catch (e: unknown) {
        if (isAbortError(e)) {
            throw e;
        }
        throw new BasemapManifestFetchError(toError(e));
    }
};

const parseManifestText = (text: string): BasemapManifest => {
    let json: unknown;
    try {
        json = JSON.parse(text);
    } catch {
        throw new BasemapManifestContentError("not valid JSON");
    }
    try {
        return parseBasemapManifest(json);
    } catch (e: unknown) {
        throw new BasemapManifestContentError(toError(e).message);
    }
};

const fetchBasemapManifest = async (signal: AbortSignal): Promise<BasemapManifest> => {
    const response = await fetchManifestResponse(signal);
    if (!response.ok) {
        throw new BasemapManifestResponseError(response.status);
    }
    return parseManifestText(await readManifestText(response));
};

let currentLoad: ManifestLoad | null = null;

class ManifestLoad {
    public readonly promise: Promise<BasemapManifest>;
    private readonly controller = new AbortController();
    private settled = false;

    constructor() {
        this.promise = fetchBasemapManifest(this.controller.signal).then(
            manifest => {
                this.settled = true;
                return manifest;
            },
            (e: unknown) => {
                this.settled = true;
                this.forget();
                throw e;
            },
        );
    }

    abort(): void {
        if (this.settled) {
            return;
        }
        this.forget();
        this.controller.abort();
    }

    private forget(): void {
        if (currentLoad === this) {
            currentLoad = null;
        }
    }
}

export const loadBasemapManifest = (): Promise<BasemapManifest> => {
    currentLoad = currentLoad ?? new ManifestLoad();
    return currentLoad.promise;
};

export const abortBasemapManifestLoad = (): void => {
    currentLoad?.abort();
};

export const TEST_resetBasemapManifestCache = (): void => {
    currentLoad = null;
};
